<script setup lang="ts">
import type { ILandingCallbackFormData } from '@/features/LandingCallback/model/types/index';
import { CALLBACK_FORM_NAME } from '@/shared/constants/callbackFormConstants';
import { LANDING_CALLBACK_REQUIRED_ERROR } from '@/features/LandingCallback/lib/landingCallbackConstants';
import { usePhoneInput } from '@/shared/composables/usePhoneInput';

defineOptions({ name: 'LandingCallback' });

const props = withDefaults(defineProps<{ isSubmitting?: boolean }>(), {
  isSubmitting: false,
});

const phoneError = defineModel<string>('phoneError', { default: '' });

const isMobile: boolean = useIsMobile();
const name: Ref<string> = ref('');
const phone: Ref<string> = ref('');

const emit = defineEmits<{
  (e: 'submit', data: ILandingCallbackFormData): void;
}>();

const {
  phoneMask,
  phoneAttr,
  onFocus: phoneFocusHandler,
  onBlur: phoneBlurHandler,
} = usePhoneInput(phone);

const isPhoneComplete: ComputedRef<boolean> = computed(
  () => phone.value.replace(/\D/g, '').length === 11,
);

const isValid: ComputedRef<boolean> = computed(() =>
  Boolean(name.value && isPhoneComplete.value),
);

const errors = reactive<Record<'name' | 'phone', string>>({ name: '', phone: '' });

const fillErrors = (): void => {
  errors.name = name.value ? '' : LANDING_CALLBACK_REQUIRED_ERROR;
  errors.phone = isPhoneComplete.value ? '' : LANDING_CALLBACK_REQUIRED_ERROR;
};

const resetError = (field: keyof typeof errors): void => {
  errors[field] = '';
  if (field === 'phone') phoneError.value = '';
};

const phoneFieldError: ComputedRef<string> = computed(
  () => errors.phone || phoneError.value,
);

const submitHandler = (): void => {
  if (props.isSubmitting) return;
  if (!isValid.value) {
    fillErrors();
    return;
  }

  const data: ILandingCallbackFormData = {
    name: name.value,
    phone: phone.value,
    from: CALLBACK_FORM_NAME,
  };
  emit('submit', data);
};
</script>

<template>
  <div
    :class="['landing-callback', { _mobile: isMobile }]"
    data-testid="landing-callback"
  >
    <div class="landing-callback__text">
      <h3 class="landing-callback__title">
        Есть <br />
        вопросы?
      </h3>
      <span class="landing-callback__subtitle">
        Давайте мы на них ответим по телефону.
      </span>
    </div>
    <form class="landing-callback__form" @submit.prevent="submitHandler">
      <div class="landing-callback__form-item">
        <AppInput
          v-model="name"
          placeholder="Введите имя..."
          type="text"
          name="name"
          :error="errors.name"
          @update:model-value="resetError('name')"
        />
      </div>
      <div class="landing-callback__form-item">
        <AppInput
          v-model="phone"
          placeholder="Введите телефон..."
          type="tel"
          name="phone"
          :mask="phoneMask"
          :attr="phoneAttr"
          :error="phoneFieldError"
          @update:model-value="resetError('phone')"
          @focus="phoneFocusHandler"
          @blur="phoneBlurHandler"
        />
      </div>
      <div class="landing-callback__form-item">
        <AppButton is-big button-text="Позвоните мне" type="submit" />
      </div>
    </form>
  </div>
</template>

<style lang="less">
.landing-callback {
  display: flex;
  justify-content: space-between;

  &__text {
    max-width: 335px;
    padding-top: @size-x5;
    margin-right: @size-x10;
  }

  &__title {
    font-size: @size-x6;
    line-height: 25px;
    font-weight: 600;
    color: #fff;
    max-width: 151px;
    margin-bottom: @size-x5;
  }

  &__subtitle {
    font-size: 14px;
    line-height: 21px;
    font-weight: 500;
    color: #fff;
  }

  &__form {
    text-align: right;
    flex: 1 1 auto;
    max-width: 420px;
  }

  &__form-item {
    width: 100%;

    &:not(:last-child) {
      margin-bottom: @size-x5;
    }

    .app-input__error {
      text-align: left;
    }
  }

  &._lawyer &__text {
    padding-top: 49px;
    margin-right: @size-x5;
  }

  &._lawyer &__title {
    max-width: 496px;
    margin-bottom: 0;
  }

  &._mobile {
    flex-wrap: wrap;
  }

  &._mobile &__form-item {
    .app-button {
      width: 100%;
    }
  }

  &._mobile &__text {
    padding-top: 0;
    margin-right: 0;
  }

  &._mobile &__form {
    width: 100%;
    margin-top: @size-x8;
  }

  &._mobile &__text-title {
    margin-bottom: 6px;
  }

  &._mobile._lawyer &__text {
    margin-right: 0;
  }

  &._mobile._lawyer &__title {
    font-size: @size-x4;
    line-height: @size-x4;
  }
}
</style>
